import { useState } from 'react';
import Layout from '../../components/feature/Layout';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from 'recharts';
import { trafficSources, sourceConversionChart, sourceDropOffChart, weeklyTrafficData } from '../../mocks/traffic';

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-border rounded-xl p-3 shadow-hover text-[12px]">
      <p className="font-semibold text-charcoal-500 mb-1">{label}</p>
      {payload.map((p: any) => (
        <p key={p.name} style={{ color: p.color || p.fill }} className="font-medium">{p.name}: {p.value}</p>
      ))}
    </div>
  );
};

export default function TrafficSources() {
  const [activeSource, setActiveSource] = useState<number | null>(null);
  const [period, setPeriod] = useState<'week' | 'month'>('week');

  const totalSessions = trafficSources.reduce((a, b) => a + b.sessions, 0);

  return (
    <Layout>
      <div className="space-y-5">
        {/* Source Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {trafficSources.slice(0, 4).map(source => (
            <div
              key={source.id}
              onClick={() => setActiveSource(activeSource === source.id ? null : source.id)}
              className={`bg-white rounded-2xl shadow-card p-5 cursor-pointer transition-all border-2 ${activeSource === source.id ? 'border-champagne-400' : 'border-transparent hover:border-champagne-200'}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl" style={{ backgroundColor: `${source.color}15` }}>
                  <i className={`${source.icon} text-[18px]`} style={{ color: source.color }} />
                </div>
                <span className="text-[11px] font-semibold text-green-500">+{source.conversionRate}%</span>
              </div>
              <p className="text-[13px] font-semibold text-charcoal-600 mb-0.5">{source.source}</p>
              <p className="text-[20px] font-bold text-charcoal-600">{source.sessions.toLocaleString()}</p>
              <p className="text-[11px] text-charcoal-300 mt-0.5">{Math.round((source.sessions / totalSessions) * 100)}% toplam trafik</p>
              <div className="mt-3 h-1.5 bg-charcoal-100 rounded-full">
                <div className="h-1.5 rounded-full transition-all" style={{ width: `${(source.sessions / trafficSources[0].sessions) * 100}%`, backgroundColor: source.color }} />
              </div>
            </div>
          ))}
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Top Sources By Conversion */}
          <div className="bg-white rounded-2xl shadow-card p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="text-[15px] font-semibold text-charcoal-600">En İyi Dönüşüm Kaynakları</h3>
                <p className="text-[12px] text-charcoal-300 mt-0.5">Dönüşüm oranı sıralaması</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={sourceConversionChart} layout="vertical" margin={{ top: 0, right: 30, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F0EDE8" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: '#A0A0A0' }} axisLine={false} tickLine={false} unit="%" domain={[0, 12]} />
                <YAxis type="category" dataKey="source" tick={{ fontSize: 11, fill: '#6B6B6B' }} axisLine={false} tickLine={false} width={80} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="convRate" name="Dönüşüm %" fill="#D4AF37" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Lowest Drop-Off */}
          <div className="bg-white rounded-2xl shadow-card p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="text-[15px] font-semibold text-charcoal-600">En Düşük Kayıp Kaynakları</h3>
                <p className="text-[12px] text-charcoal-300 mt-0.5">Bounce oranı sıralaması</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={sourceDropOffChart} layout="vertical" margin={{ top: 0, right: 30, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="dropOffGrad" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#A5D6A7" />
                    <stop offset="100%" stopColor="#E57373" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#F0EDE8" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: '#A0A0A0' }} axisLine={false} tickLine={false} unit="%" domain={[0, 55]} />
                <YAxis type="category" dataKey="source" tick={{ fontSize: 11, fill: '#6B6B6B' }} axisLine={false} tickLine={false} width={80} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="dropOff" name="Kayıp %" fill="url(#dropOffGrad)" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Weekly Traffic by Source */}
        <div className="bg-white rounded-2xl shadow-card p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="text-[15px] font-semibold text-charcoal-600">Haftalık Kaynak Dağılımı</h3>
              <p className="text-[12px] text-charcoal-300 mt-0.5">Günlük trafik kaynak bazlı</p>
            </div>
            <div className="flex gap-1 bg-[#FAF8F4] rounded-full p-1">
              {(['week', 'month'] as const).map(p => (
                <button key={p} onClick={() => setPeriod(p)} className={`px-3 py-1 rounded-full text-[12px] font-medium transition-all cursor-pointer whitespace-nowrap ${period === p ? 'bg-white text-champagne-600 shadow-sm' : 'text-charcoal-400 hover:text-charcoal-600'}`}>
                  {p === 'week' ? 'Hafta' : 'Ay'}
                </button>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={weeklyTrafficData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F0EDE8" vertical={false} />
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#A0A0A0' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#A0A0A0' }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="google" name="Google" fill="#D4AF37" stackId="a" radius={[0,0,0,0]} />
              <Bar dataKey="instagram" name="Instagram" fill="#E4405F" stackId="a" />
              <Bar dataKey="facebook" name="Facebook" fill="#7CB342" stackId="a" />
              <Bar dataKey="organic" name="Organik" fill="#64B5F6" stackId="a" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
          <div className="flex items-center gap-4 mt-3 flex-wrap">
            {[{ n: 'Google', c: '#D4AF37' }, { n: 'Instagram', c: '#E4405F' }, { n: 'Facebook', c: '#7CB342' }, { n: 'Organik', c: '#64B5F6' }].map(s => (
              <div key={s.n} className="flex items-center gap-1.5 text-[11px] text-charcoal-400">
                <span className="w-3 h-2 rounded-sm inline-block" style={{ backgroundColor: s.c }} />
                {s.n}
              </div>
            ))}
          </div>
        </div>

        {/* Traffic Source Table */}
        <div className="bg-white rounded-2xl shadow-card overflow-hidden">
          <div className="px-6 py-4 border-b border-border">
            <h3 className="text-[15px] font-semibold text-charcoal-600">Tüm Trafik Kaynakları</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#FAF8F4] border-b border-border">
                  {['Kaynak', 'Medium', 'Oturum', 'Dönüşüm', 'Dönüşüm Oranı', 'Ort. Süre', 'Event/Oturum', 'Bounce Oranı'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-[11px] font-semibold text-charcoal-300 uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {trafficSources.map((row, idx) => (
                  <tr key={row.id} className={`border-b border-border hover:bg-[#FAF8F4] transition-colors ${idx % 2 === 0 ? '' : 'bg-[#FDFCFA]'}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 flex items-center justify-center rounded-lg" style={{ backgroundColor: `${row.color}15` }}>
                          <i className={`${row.icon} text-[13px]`} style={{ color: row.color }} />
                        </div>
                        <span className="text-[13px] font-semibold text-charcoal-600">{row.source}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[12px] text-charcoal-400">{row.medium}</td>
                    <td className="px-4 py-3 text-[13px] font-medium text-charcoal-500">{row.sessions.toLocaleString()}</td>
                    <td className="px-4 py-3 text-[13px] font-medium text-charcoal-500">{row.conversions}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold" style={{ backgroundColor: '#D4AF3718', color: '#D4AF37' }}>
                        {row.conversionRate}%
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[12px] text-charcoal-500 font-mono">{row.avgSessionDuration}</td>
                    <td className="px-4 py-3 text-[12px] text-charcoal-500">{row.eventsPerSession}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-charcoal-100 rounded-full h-1.5 max-w-[50px]">
                          <div className="h-1.5 rounded-full bg-red-300" style={{ width: `${(row.bounceRate / 50) * 100}%` }} />
                        </div>
                        <span className="text-[12px] text-charcoal-500">{row.bounceRate}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}
